// Marges — analyse de rentabilité produit
import { useState, useMemo } from "react";
import { TrendingUp, AlertCircle, Search } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { KPICard } from "@/components/admin/shared/KPICard";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import { useMargins } from "@/hooks/admin/useFinance";
import type { MarginProduct } from "@/lib/admin-api/finance";

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatFcfa(amount: number | null | undefined): string {
  const n = Number(amount);
  if (isNaN(n)) return "—";
  return `${n.toLocaleString("fr-SN")} FCFA`;
}

function marginColorClass(pct: number): string {
  if (pct >= 30) return "text-emerald-700 font-semibold";
  if (pct >= 15) return "text-amber-700 font-semibold";
  return "text-red-700 font-semibold";
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function Margins() {
  const [search, setSearch] = useState("");
  const [brandFilter, setBrandFilter] = useState("");
  const [page, setPage] = useState(1);

  const params = useMemo(
    () => ({
      q: search || null,
      brand: brandFilter || null,
      page,
      limit: 50,
    }),
    [search, brandFilter, page],
  );

  const { data, isLoading, isError, refetch } = useMargins(params);

  const products = data?.products ?? [];
  const avgMargin = data?.avg_margin_percent ?? 0;

  const topProduct = useMemo(() => {
    if (products.length === 0) return "—";
    return [...products].sort((a, b) => b.margin_percent - a.margin_percent)[0]
      ?.name ?? "—";
  }, [products]);

  const columns: readonly ColumnDef<MarginProduct>[] = useMemo(
    () => [
      {
        id: "sku",
        header: "SKU",
        cell: (p) => (
          <span className="font-mono text-xs text-stone-500">{p.sku}</span>
        ),
      },
      {
        id: "name",
        header: "Produit",
        cell: (p) => (
          <span className="text-sm font-medium text-stone-900">{p.name}</span>
        ),
      },
      {
        id: "brand",
        header: "Marque",
        cell: (p) => (
          <span className="rounded-full bg-stone-100 px-2.5 py-0.5 text-xs font-medium text-stone-700">
            {p.brand}
          </span>
        ),
      },
      {
        id: "sell_price_fcfa",
        header: "Prix vente",
        cell: (p) => (
          <span className="text-sm text-stone-700">
            {formatFcfa(p.sell_price_fcfa)}
          </span>
        ),
      },
      {
        id: "cost_price_fcfa",
        header: "Coût",
        cell: (p) => (
          <span className="text-sm text-stone-500">
            {formatFcfa(p.cost_price_fcfa)}
          </span>
        ),
      },
      {
        id: "margin_fcfa",
        header: "Marge (FCFA)",
        cell: (p) => (
          <span className="text-sm font-medium text-stone-900">
            {formatFcfa(p.margin_fcfa)}
          </span>
        ),
      },
      {
        id: "margin_percent",
        header: "Marge %",
        sortable: true,
        cell: (p) => (
          <span className={`text-sm ${marginColorClass(p.margin_percent)}`}>
            {(p.margin_percent ?? 0).toFixed(1)}%
          </span>
        ),
      },
      {
        id: "units_sold",
        header: "Unités vendues",
        cell: (p) => (
          <span className="text-sm text-stone-600">
            {(p.units_sold ?? 0).toLocaleString("fr-SN")}
          </span>
        ),
      },
      {
        id: "revenue_fcfa",
        header: "CA (FCFA)",
        cell: (p) => (
          <span className="text-sm font-semibold text-stone-900">
            {formatFcfa(p.revenue_fcfa)}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
            <TrendingUp className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-xl font-semibold text-stone-900">
              Marges produits
            </h1>
            <p className="text-sm text-stone-500">
              {isLoading
                ? "Chargement…"
                : `${data?.pagination.total ?? 0} produit(s)`}
            </p>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-2">
          <KPICard
            label="Marge moyenne"
            value={avgMargin}
            format="percent"
            icon={<TrendingUp className="h-4 w-4" />}
          />
          <KPICard
            label="Produit le plus rentable"
            value={topProduct}
            icon={<TrendingUp className="h-4 w-4" />}
          />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Rechercher produit ou SKU…"
              className="h-9 w-full rounded-md border border-stone-200 pl-9 pr-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            />
          </div>
          <input
            type="text"
            value={brandFilter}
            onChange={(e) => { setBrandFilter(e.target.value); setPage(1); }}
            placeholder="Filtrer par marque…"
            className="h-9 rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
          {(search || brandFilter) && (
            <button
              type="button"
              onClick={() => { setSearch(""); setBrandFilter(""); setPage(1); }}
              className="text-sm text-stone-500 hover:text-stone-700"
            >
              Réinitialiser
            </button>
          )}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-4 text-xs text-stone-500">
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-2.5 w-2.5 rounded-full bg-emerald-500" />
            Marge ≥ 30%
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-2.5 w-2.5 rounded-full bg-amber-400" />
            Marge 15–29%
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-2.5 w-2.5 rounded-full bg-red-500" />
            Marge &lt; 15%
          </span>
        </div>

        {/* Error state */}
        {isError && (
          <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>Impossible de charger les marges produits.</span>
            <button
              type="button"
              onClick={() => refetch()}
              className="ml-auto rounded border border-red-200 bg-white px-3 py-1 text-xs text-red-700 hover:bg-red-50"
            >
              Réessayer
            </button>
          </div>
        )}

        {/* Table */}
        {!isError && (
          <>
            {!isLoading && products.length === 0 ? (
              <EmptyState
                icon={<TrendingUp className="h-6 w-6" />}
                title="Aucun produit"
                description="Aucun résultat pour ces critères de recherche."
              />
            ) : (
              <AdminDataTable
                data={products}
                columns={columns}
                isLoading={isLoading}
                pagination={
                  data?.pagination
                    ? {
                        page: data.pagination.page,
                        limit: data.pagination.limit,
                        total: data.pagination.total,
                      }
                    : undefined
                }
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
